import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import { Close, ContentCopy } from "@mui/icons-material"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink } from "react-router-dom"
import moment from "moment"
import { deriveMigrationStatus } from "shared/sstv2MigrationData"
import Sstv2LifecycleStepper from "./Sstv2LifecycleStepper"
import Sstv2MigrationAttemptStatePanel from "./Sstv2MigrationAttemptStatePanel"
import Sstv2MigrationFailuresPanel from "./Sstv2MigrationFailuresPanel"
import Sstv2MigrationJobProgress from "./Sstv2MigrationJobProgress"
import { Sstv2TermChip } from "./Sstv2TermLabel"
import {
  Sstv2MigrationRow,
  isRollbackLifecycleEvent,
  parseNaiveUtcMs,
  phaseChipColor,
} from "./sstv2MigrationTypes"
import { describePhase } from "./sstv2Vocabulary"

const labelSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase" as const,
  color: "text.secondary",
}

const InfoRow: FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ textAlign: "right", wordBreak: "break-all" }}>{value}</Box>
  </Stack>
)

/**
 * Parse a partner naive-UTC timestamp (no trailing Z) as UTC. Append `Z`
 * before `new Date(...)` per memory `lambda-locale-date-roundtrip`.
 */
const parseNaiveUtc = (ts: string): moment.Moment => {
  if (!ts) return moment.invalid()
  const z = /[zZ]|[+-]\d\d:?\d\d$/.test(ts) ? ts : `${ts}Z`
  return moment(z)
}

export const Sstv2MigrationDrawer: FC<{
  row: Sstv2MigrationRow | null
  onClose: () => void
}> = ({ row, onClose }) => {
  const { t } = useTranslation()
  // The JOB id, narrowed once for the whole render: records written before the
  // 2026-07-28 re-key have none, and everything id-shaped below is conditional
  // on it (nothing to copy, no traces to filter).
  const migrationId = row?.migrationId

  // Timeline sorted by partner timestamp, each annotated with whether its
  // ARRIVAL order (receivedAt) differs from its timestamp order (Item 6). A
  // mismatch means the event was delivered out of sequence — the signal that
  // distinguishes "partner is slow" from "our ingestion is stuck".
  const timeline = useMemo(() => {
    if (!row) return []
    const events = [...(row.source.events || [])].sort(
      (a, b) => parseNaiveUtcMs(a.timestamp) - parseNaiveUtcMs(b.timestamp),
    )
    const allHaveReceivedAt = events.every((e) => !!e.receivedAt)
    // Rank each event by receivedAt; compare to its timestamp-rank (index).
    const byReceived = [...events].sort(
      (a, b) => Date.parse(a.receivedAt) - Date.parse(b.receivedAt),
    )
    const receivedRank = new Map(byReceived.map((e, i) => [e, i]))
    return events.map((e, tsRank) => ({
      ...e,
      outOfOrder: allHaveReceivedAt && receivedRank.get(e) !== tsRank,
    }))
  }, [row])

  // Spec-shaped status summary ({status, started_at, finished_at, last_step}),
  // derived from the folded entity so the drawer matches the partner's
  // `GET /migration/status` vocabulary.
  const status = useMemo(
    () => deriveMigrationStatus(row?.source ?? null),
    [row],
  )

  const fmtTs = (ts?: string) =>
    ts ? `${parseNaiveUtc(ts).format("YYYY-MM-DD HH:mm:ss")} UTC` : "—"

  return (
    <Drawer data-ui="UI-SL2W-257" anchor="right" open={!!row} onClose={onClose}>
      {/* Wide enough for the 6-column failed-reservations table (monospace
          24-char reservation ids) without horizontal scrolling on a laptop.
          Capped at the viewport so it degrades to full-bleed on narrow screens;
          the table keeps its own overflow-x for anything still too wide. */}
      <Box
        sx={{
          width: { xs: "100vw", sm: 640, md: 900, lg: 1080 },
          maxWidth: "100vw",
          p: 3,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" fontWeight={700}>
            {t("migrationDetail")}
          </Typography>
          <IconButton onClick={onClose} aria-label={t("close")} size="small">
            <Close />
          </IconButton>
        </Stack>

        {row && (
          <Stack spacing={2}>
            {/* Narrowed once: pre-2026-07-28 records carry no job id. */}
            <Stack spacing={1.25}>
              <InfoRow label={t("tenantName")} value={row.tenantName} />
              {/* The partner's phase, glossed in place. It is routinely confused
                  with our own pipeline state further down the drawer — most of all
                  partner COMPLETED (they finished SENDING history) against our
                  COMPLETE (we finished WRITING it), which since D37 can be hours
                  apart. An unmodelled phase renders as "Unknown (RAW)". */}
              <InfoRow
                label={t("migrationPhase")}
                value={
                  <Sstv2TermChip
                    term={describePhase(row.phase)}
                    color={phaseChipColor(row.phase)}
                  />
                }
              />
              <InfoRow
                label={t("migrationStartedAt")}
                value={
                  <Typography variant="body2">
                    {fmtTs(status.started_at)}
                  </Typography>
                }
              />
              <InfoRow
                label={t("migrationFinishedAt")}
                value={
                  <Typography variant="body2">
                    {fmtTs(status.finished_at)}
                  </Typography>
                }
              />
              <InfoRow
                label={t("migrationId")}
                value={
                  migrationId ? (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <Typography variant="body2" fontFamily="monospace">
                        {migrationId}
                      </Typography>
                      <Tooltip title={t("copy")}>
                        <IconButton
                          size="small"
                          aria-label={t("copy")}
                          onClick={() =>
                            void navigator.clipboard?.writeText(migrationId)
                          }
                        >
                          <ContentCopy sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Tooltip title={t("migrationLegacyRecordHint")}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t("migrationLegacyRecord")}
                        sx={{ height: 20, fontSize: "0.68rem" }}
                      />
                    </Tooltip>
                  )
                }
              />
              <InfoRow
                label={t("migrationV1Locker")}
                value={
                  <Typography variant="body2" fontFamily="monospace" noWrap>
                    {row.v1LockerName} ({row.sl1LockerId})
                  </Typography>
                }
              />
              {row.attemptCount > 1 && (
                <InfoRow
                  label={t("migrationAttempt")}
                  value={
                    <Typography variant="body2">
                      {t("migrationAttemptNofM", {
                        n: row.attemptIndex,
                        m: row.attemptCount,
                      })}
                    </Typography>
                  }
                />
              )}
              {row.createdSl2LockerThingId && (
                <InfoRow
                  label={t("migrationCreatedLocker")}
                  value={
                    <Link
                      component={RouterLink}
                      to={`/sa/lockers/${row.createdSl2LockerThingId}`}
                    >
                      {t("viewLocker")}
                    </Link>
                  }
                />
              )}
            </Stack>

            <Divider />

            {/* Lifecycle stepper (Item 5) — where the locker is + what's next. */}
            <Box>
              <Typography sx={{ ...labelSx, mb: 1 }}>
                {t("migrationLifecycleStep")}
              </Typography>
              <Sstv2LifecycleStepper row={row} />
            </Box>

            <Divider />

            {/* Migration JOB progress (DEV-193 §6) — pipeline state + the two
                reservation progress bars + advisory discrepancy banner. */}
            <Sstv2MigrationJobProgress row={row} />

            <Divider />

            {/* ATTEMPT STATE — what we actually DID, as opposed to what the
                partner told us. Placed directly after the counters on purpose:
                it is the panel that reinterprets them. A COMPLETE job with
                unflipped mappings and no live execution looks fine above and
                wrong here, and here is the truth. */}
            <Sstv2MigrationAttemptStatePanel row={row} />

            <Divider />

            {/* Which reservations did not make it, and why — partner-reported
                payload failures + our own flip/adopt failures. */}
            <Sstv2MigrationFailuresPanel row={row} />

            <Divider />

            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography sx={labelSx}>{t("migrationTimeline")}</Typography>
                {/* The partner timeline is what we FOLDED; the trace list is every
                    event as received, with steps and errors. One click across —
                    only for a record that HAS a job id to filter traces by. */}
                {migrationId && (
                  <Link
                    component={RouterLink}
                    to={`/sa/sstv2?tab=6&migrationId=${encodeURIComponent(
                      migrationId,
                    )}`}
                    variant="caption"
                  >
                    {t("migrationViewTraces")}
                  </Link>
                )}
              </Stack>
              {timeline.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t("migrationNoEvents")}
                </Typography>
              )}
              <Stack spacing={1.5}>
                {timeline.map((ev, i) => (
                  <Stack
                    key={`${ev.event}-${ev.timestamp}-${i}`}
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-start"
                  >
                    <Box
                      sx={{
                        mt: 0.75,
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        flexShrink: 0,
                        bgcolor: isRollbackLifecycleEvent(ev.event)
                          ? "error.main"
                          : "primary.main",
                      }}
                    />
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontFamily="monospace">
                          {ev.event}
                        </Typography>
                        {ev.outOfOrder && (
                          <Chip
                            size="small"
                            color="info"
                            variant="outlined"
                            label={t("migrationOutOfOrder")}
                            sx={{ height: 18, fontSize: "0.62rem" }}
                          />
                        )}
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {t("migrationEventTs")}:{" "}
                        {parseNaiveUtc(ev.timestamp).format(
                          "YYYY-MM-DD HH:mm:ss",
                        )}{" "}
                        UTC
                      </Typography>
                      {ev.receivedAt && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {t("migrationReceivedAt")}:{" "}
                          {parseNaiveUtc(ev.receivedAt).format(
                            "YYYY-MM-DD HH:mm:ss",
                          )}{" "}
                          UTC
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
      </Box>
    </Drawer>
  )
}

export default Sstv2MigrationDrawer
